import { useState } from 'react';
import { Button } from '@vnetwork/ui/primitives';
import type { ActivityMetadataDiff } from '@/api/customer-activity';

/**
 * Renders a `{ old, new }` field-diff as "Label: old → new" rows — old muted,
 * new in success tone. Shows the first 2 changed fields, then a ghost toggle
 * expands the rest (mirrors legacy "+N thay đổi khác"). Fields with no real
 * change and noisy audit keys are dropped.
 */

/** Audit/plumbing keys that carry no user-meaningful change. */
const HIDDEN_KEYS = new Set([
  'id',
  'created_at',
  'updated_at',
  'deleted_at',
  'service_template',
  'service_creatable_uuid',
  'contact_source_updated_at',
]);

/** Known field-key → EN label. Anything else is humanized from the key. */
const FIELD_LABELS: Record<string, string> = {
  contact_phone: 'Phone',
  contact_email: 'Email',
  contact_name: 'Name',
  contact_industry: 'Industry',
  contact_position: 'Position',
  contact_source: 'Source',
  contact_address: 'Address',
  customer_name: 'Customer name',
  customer_type: 'Customer type',
  customer_status: 'Status',
  lead_status: 'Lead status',
  journey_stage: 'Journey stage',
  note: 'Note',
  tax_code: 'Tax code',
  company_name: 'Company',
  website: 'Website',
};

/** Title-case a snake/kebab key: `contact_phone` → "Contact phone". */
function humanizeKey(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Format a diff value for display. Empty/null → em-dash marker (handled by caller). */
function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    return value
      .map((v) =>
        v && typeof v === 'object'
          ? String((v as Record<string, unknown>).name ?? (v as Record<string, unknown>).label ?? '')
          : String(v),
      )
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    for (const k of ['name', 'title', 'label', 'email', 'value']) {
      if (o[k]) return String(o[k]);
    }
    const keys = Object.keys(o).filter((k) => !HIDDEN_KEYS.has(k));
    return keys.length ? `${keys.length} fields` : '';
  }
  return String(value);
}

/** Structural equality good enough for diff pruning (primitives + JSON compare). */
function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' && typeof b !== 'object') return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

function DiffRow({ label, oldVal, newVal }: { label: string; oldVal: unknown; newVal: unknown }) {
  const oldText = formatValue(oldVal);
  const newText = formatValue(newVal);
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-1) 0',
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      <span style={{ color: 'var(--vn-fg-muted)', minWidth: 88, fontWeight: 'var(--vn-font-weight-medium)' }}>
        {label}
      </span>
      <span
        style={{
          color: 'var(--vn-fg-subtle)',
          textDecoration: oldText ? 'line-through' : 'none',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {oldText || '—'}
      </span>
      <span aria-hidden style={{ color: 'var(--vn-fg-subtle)' }}>→</span>
      <span
        style={{
          color: 'var(--vn-success-text)',
          fontWeight: 'var(--vn-font-weight-medium)',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {newText || '—'}
      </span>
    </div>
  );
}

export function ActivityMetadataDiff({ metadata }: { metadata: ActivityMetadataDiff | null }) {
  const [open, setOpen] = useState(false);

  const oldObj = metadata?.old ?? null;
  const newObj = metadata?.new ?? null;
  if (!oldObj && !newObj) return null;

  // Union of keys across old+new so added/removed fields still show.
  const keys = Array.from(new Set([...Object.keys(oldObj ?? {}), ...Object.keys(newObj ?? {})]))
    .filter((k) => !HIDDEN_KEYS.has(k))
    .filter((k) => !isEqual(oldObj?.[k], newObj?.[k]));

  if (keys.length === 0) return null;

  const visible = open ? keys : keys.slice(0, 2);
  const hidden = keys.length - visible.length;

  return (
    <div style={{ marginTop: 'var(--vn-space-2)' }}>
      {visible.map((k) => (
        <DiffRow key={k} label={humanizeKey(k)} oldVal={oldObj?.[k]} newVal={newObj?.[k]} />
      ))}
      {hidden > 0 && (
        <Button variant="ghost" size="sm" onClick={() => setOpen(true)} style={{ marginTop: 'var(--vn-space-1)' }}>
          ＋{hidden} more {hidden === 1 ? 'change' : 'changes'}
        </Button>
      )}
      {open && keys.length > 2 && (
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)} style={{ marginTop: 'var(--vn-space-1)' }}>
          Show less
        </Button>
      )}
    </div>
  );
}

export default ActivityMetadataDiff;
